import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useState, useRef } from 'react';
import * as Models from 'models';
import { useEditModeContext } from '../EditModeContextPlugin';
import { CheckSource, areBrandStyleChangedMapsEqual, $buildBrandStyleChangedBySource } from './utils';

type Props = {
  brandStyle: Models.BrandStyleMap;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  onBrandStyleChanged: (changed: boolean) => void;
};

export function BrandStyleChangedPlugin({ brandStyle, fonts, colors, onBrandStyleChanged }: Props): null {
  const [editor] = useLexicalComposerContext();
  const editMode = useEditModeContext();
  const [brandStyleChangedBySource, setBrandStyleChangedBySource] = useState<Partial<Record<CheckSource, boolean>>>({});
  const brandStyleChangedBySourceRef = useRef<Partial<Record<CheckSource, boolean>>>({});
  const hasPerformedChecks = Object.keys(brandStyleChangedBySource).length > 0;
  const isBrandStyleChanged = Object.values(brandStyleChangedBySource).some(Boolean);

  useEffect(() => {
    brandStyleChangedBySourceRef.current = brandStyleChangedBySource;
  }, [brandStyleChangedBySource]);

  useEffect(() => {
    if (!editMode) {
      return;
    }
    if (!hasPerformedChecks) {
      return;
    }
    onBrandStyleChanged(isBrandStyleChanged);
  }, [onBrandStyleChanged, isBrandStyleChanged, editMode, hasPerformedChecks]);

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const $runChecks = (): void => {
      const nextBrandStyleChangedBySource = $buildBrandStyleChangedBySource({ brandStyle, colors, fonts });

      if (areBrandStyleChangedMapsEqual(brandStyleChangedBySourceRef.current, nextBrandStyleChangedBySource)) {
        return;
      }

      brandStyleChangedBySourceRef.current = nextBrandStyleChangedBySource;
      setBrandStyleChangedBySource(nextBrandStyleChangedBySource);
    };

    editor.getEditorState().read($runChecks);

    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) {
        return;
      }

      editorState.read($runChecks);
    });

  }, [editor, editMode, brandStyle, colors, fonts]);

  return null;
}
